import { startTransition, useMemo, useState } from 'react';
import Files from 'react-files';
import { useDispatch, useSelector } from 'react-redux';
import { Virtuoso } from 'react-virtuoso';
import { Col, Divider, Form, Row } from 'antd';
import { sumBy } from 'es-toolkit';

import { convertBlobToBase64 } from '@/utils/file';
import { generateUUIDv4 } from '@/utils/number';

import { ReactComponent as FileIcon } from '../../../../../../assets/icons/files-records.svg';
import { FILES_ERROR_CODES } from '../../../../../../contants/common';
import { getOverChargeItemsFromExcelInProgress } from '../../../../../../redux/Claims/OverCharge/OverChargeActions';
import { setAppLoader, setWarningModal } from '../../../../../../redux/common/CommonActions';
import { convertToFloat, formateAmount } from '../../../../../../utilz';
import VerticalLine from '../../../../../components/Bars/VerticalLine';
import Button from '../../../../../components/Buttons/Button';
import SvgConverter from '../../../../../components/Common/SvgConverter';
import FormInput from '../../../../../components/Inputs/FormInput';
import PrimaryLabel from '../../../../../components/Labels/PrimaryLabel';
import { NOTIFICATION_TYPES, showNotification } from '../../../../../components/Modals/notification';
import Paper from '../../../../../components/Paper/Paper';
import FooterContentCard from '../../../../common/FooterContentCard';
import OverChargeItemsCard from '../../Components/Edit/Components/OverChargeItems/OverChargeItemsCard';
import OverChargeUploadItemsModal from '../../Components/Edit/Components/OverChargeItems/OverChargeUploadItemsModal';

const ITEMS_VIRTUALIZATION_THRESHOLD = 150;
const ITEM_CARD_HEIGHT = 240;
const LIST_HEIGHT_PX = 600;

const getItemKey = (item) => item.tempId || item.itemID;

const CreateOverChargeItems = ({ itemList, setItemList }) => {
  const dispatch = useDispatch();
  const [overchargePercentForm] = Form.useForm();
  const [uploadModal, setUploadModal] = useState(false);
  const { itemsFromExcel } = useSelector((state) => state.overChargeClaims);

  const createNewItem = () => ({
    billedAmount: convertToFloat(0),
    correctCharges: convertToFloat(0),
    dirtyFlag: 'i',
    editMode: true,
    fbOcKey: 0,
    invoiceNumber: undefined,
    isNew: true,
    itemID: 0,
    overchargeAmount: 0,
    packageNumber: undefined,
    paidAmount: convertToFloat(0),
    proNo: undefined,
    recoveryAmount: convertToFloat(0),
    shipDate: undefined,
    tempId: `tempId-${generateUUIDv4()}`,
    weight: 0,
  });

  const handleAddNew = (itemKey) => {
    setItemList((prev) => {
      const insertIndex = prev.findIndex((item) => getItemKey(item) === itemKey);
      if (insertIndex === -1) {
        return prev;
      }

      const newItem = createNewItem();
      const updated = [...prev];
      updated.splice(insertIndex + 1, 0, newItem);
      return updated;
    });
  };

  const handleUploadFile = async (data) => {
    try {
      if (data.length > 0) {
        const handleSuccess = () => {
          setUploadModal(true);
        };
        dispatch(setAppLoader(true));
        const file = await convertBlobToBase64(data[0]);
        dispatch(
          getOverChargeItemsFromExcelInProgress({
            fbocKey: 0,
            file: file.split(',')[1],
            handleSuccess,
          }),
        );
      }
    } catch {
      dispatch(setAppLoader(false));
      dispatch(
        setWarningModal({
          description: 'Invalid file, Please verify the selected file.',
          open: true,
        }),
      );
    }
  };

  const handleUploadFileError = (e) => {
    let errorMessage = e.message;
    if (e.code === FILES_ERROR_CODES.INVALID_FILE_TYPE) {
      errorMessage += '. Only .xlsx and .xls files can be uploaded.';
    }
    dispatch(setWarningModal({ description: errorMessage, open: true }));
  };

  const setEditMode = (itemKey, val) => {
    setItemList((prev) => prev.map((item) => (getItemKey(item) === itemKey ? { ...item, editMode: val } : item)));
  };

  const handleCancel = (itemKey, isNew) => {
    if (isNew) {
      setItemList((prev) => {
        if (prev.length <= 1) {
          showNotification({
            message: 'At least one claim item is required. Cannot cancel.',
            type: NOTIFICATION_TYPES.ERROR,
          });
          return prev;
        }
        return prev.filter((item) => getItemKey(item) !== itemKey);
      });
    } else {
      setItemList((prev) => prev.map((item) => (getItemKey(item) === itemKey ? { ...item, editMode: false } : item)));
    }
  };

  const handleSubmit = (data, itemKey) => {
    setItemList((prev) =>
      prev.map((item) => (getItemKey(item) === itemKey ? { ...item, ...data, editMode: false, isNew: false } : item)),
    );
  };

  const handleDelete = (itemKey) => {
    setItemList((prev) => {
      if (prev.length <= 1) {
        showNotification({
          message: 'At least one claim item is required. Cannot delete.',
          type: NOTIFICATION_TYPES.ERROR,
        });
        return prev;
      }
      return prev.filter((item) => getItemKey(item) !== itemKey);
    });
  };

  const overChargePercent = Form.useWatch('overChargePercent', overchargePercentForm);

  const handleOverChargePercentCalculation = () => {
    const percent = Number(overChargePercent) || 0;
    setItemList((prev) =>
      prev.map((row) => ({
        ...row,
        recoveryAmount: ((row.overchargeAmount || 0) * percent) / 100,
      })),
    );
  };

  const handleUploadApply = (paramItems) => {
    const items = paramItems.map((row) => {
      const paidAmountL = row.paidAmount || 0;
      const correctChargesL = row.correctCharges || 0;
      let diffAmount = paidAmountL - correctChargesL;
      diffAmount = diffAmount >= 0 ? diffAmount : diffAmount * -1;
      return { ...row, isNew: false, overchargeAmount: diffAmount };
    });
    setUploadModal(false);

    if (typeof startTransition !== 'undefined') {
      startTransition(() => {
        setItemList(items);
      });
    } else {
      setTimeout(() => {
        setItemList(items);
      }, 0);
    }
  };

  const footerCalculations = useMemo(() => {
    if (!itemList || itemList.length === 0) {
      return {
        billedAmount: 0,
        correctCharges: 0,
        overchargeAmount: 0,
        paidAmount: 0,
        recoveryAmount: 0,
      };
    }
    return {
      billedAmount: sumBy(itemList, ({ billedAmount }) => Number(billedAmount || 0)),
      correctCharges: sumBy(itemList, ({ correctCharges }) => Number(correctCharges || 0)),
      overchargeAmount: sumBy(itemList, ({ overchargeAmount }) => Number(overchargeAmount || 0)),
      paidAmount: sumBy(itemList, ({ paidAmount }) => Number(paidAmount || 0)),
      recoveryAmount: sumBy(itemList, ({ recoveryAmount }) => Number(recoveryAmount || 0)),
    };
  }, [itemList]);

  const shouldVirtualize = itemList.length > ITEMS_VIRTUALIZATION_THRESHOLD;

  return (
    <div>
      <Row className="align-items-center" justify="space-between">
        <Col>
          <PrimaryLabel className="fw-700 f-18">Overcharge Claim Items</PrimaryLabel>
        </Col>
        <Col>
          <Row justify="end" gutter={16} align="middle">
            <Col>
              <Files
                className="files-dropzone"
                onChange={handleUploadFile}
                onError={handleUploadFileError}
                accepts={['.xls', '.xlsx']}
                multiple={false}
                minFileSize={0}
                clickable
              >
                <Button>
                  <SvgConverter Icon={FileIcon} color="white" width={16} height={16} className="mr-5" />
                  Upload File
                </Button>
              </Files>
            </Col>
            <Col>
              <Form form={overchargePercentForm}>
                <FormInput name="overChargePercent" inputType="number" editMode margin="m-0" placeholder=" " />
              </Form>
            </Col>
            <Col>
              <Button disabled onClick={handleOverChargePercentCalculation}>
                Apply
              </Button>
            </Col>
          </Row>
        </Col>
      </Row>
      <Divider className="mt-0 my-16 ml-n16 vw-100 " />
      <div className="flex-1">
        {shouldVirtualize ? (
          <div className="custom-scroll-bar" style={{ height: '70vh', minHeight: LIST_HEIGHT_PX }}>
            <Virtuoso
              style={{ height: '100%' }}
              totalCount={itemList.length}
              defaultItemHeight={ITEM_CARD_HEIGHT}
              itemKey={(index) => getItemKey(itemList[index])}
              itemContent={(index) => {
                const item = itemList[index];
                const itemKey = getItemKey(item);
                return (
                  <OverChargeItemsCard
                    noteDetails={item}
                    itemKey={itemKey}
                    editMode={Boolean(item.editMode)}
                    setEditMode={setEditMode}
                    handleCancel={handleCancel}
                    handleSubmit={handleSubmit}
                    handleDelete={handleDelete}
                    handleAddNew={handleAddNew}
                    totalItemsCount={itemList.length}
                  />
                );
              }}
            />
          </div>
        ) : (
          <div className="custom-scroll-bar">
            {itemList.map((row) => {
              const itemKey = getItemKey(row);
              return (
                <OverChargeItemsCard
                  noteDetails={row}
                  key={itemKey}
                  itemKey={itemKey}
                  editMode={Boolean(row.editMode)}
                  setEditMode={setEditMode}
                  handleCancel={handleCancel}
                  handleSubmit={handleSubmit}
                  handleDelete={handleDelete}
                  handleAddNew={handleAddNew}
                  totalItemsCount={itemList.length}
                />
              );
            })}
          </div>
        )}
      </div>
      <Paper className="background-header px-16 py-5">
        <Row justify="space-around" className="px-10" gutter={10}>
          <Col>
            <FooterContentCard
              reverse
              align="middle"
              count={formateAmount(footerCalculations.billedAmount)}
              label="Billed Amount"
              contentClass="text-white f-16 fw-600"
              labelClass="text-light f-14"
            />
          </Col>
          <Col>
            <VerticalLine />
          </Col>
          <Col>
            <FooterContentCard
              reverse
              align="middle"
              count={formateAmount(footerCalculations.paidAmount)}
              label="Paid Amount"
              contentClass="text-white f-16 fw-600"
              labelClass="text-light f-14"
            />
          </Col>
          <Col>
            <VerticalLine />
          </Col>
          <Col>
            <FooterContentCard
              reverse
              align="middle"
              count={formateAmount(footerCalculations.correctCharges)}
              label="Correct Charges"
              contentClass="text-white f-16 fw-600"
              labelClass="text-light f-14"
            />
          </Col>
          <Col>
            <VerticalLine />
          </Col>
          <Col>
            <FooterContentCard
              reverse
              align="middle"
              count={formateAmount(footerCalculations.overchargeAmount)}
              label="Overcharge Sum"
              contentClass="text-white f-16 fw-600"
              labelClass="text-light f-14"
            />
          </Col>
          <Col>
            <VerticalLine />
          </Col>
          <Col>
            <FooterContentCard
              reverse
              align="middle"
              count={formateAmount(footerCalculations.recoveryAmount)}
              label="Recovery Sum"
              contentClass="text-white f-16 fw-600"
              labelClass="text-light f-14"
            />
          </Col>
        </Row>
      </Paper>
      <OverChargeUploadItemsModal
        open={uploadModal}
        setOpen={setUploadModal}
        items={itemsFromExcel?.data || []}
        handleApply={handleUploadApply}
      />
    </div>
  );
};

export default CreateOverChargeItems;
